import { CustomImage } from '@app/components/various/CustomImage';
import { Tooltip } from '@app/components/various/Tooltip';
import { getDefaultImageSize, getFilteredDelwinIds } from '@app/ducks';
import { getDefaultImageAspectRatio, getProductImageOrPlaceholderBySize } from '@app/ducks/helpers';
import ProductsLogic, { getUrlForProductAndVariant, getVariantsForMatrix } from '@app/logic/products';
import { isSwitchAvailable } from '@app/logic/unitsSwitch';
import { useDelwinIdsWithCurrentStockType } from '@app/utils/hooks';
import { useIsOverflowing } from '@app/utils/hooks/useIsOverflowing';
import { useOffsetWidth } from '@app/utils/hooks/useOffsetWidth';
import { isEmpty } from '@app/utils/isEmpty';
import { Product } from '@typings';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { ProductItemContextProvider, useProductItemContext } from '../context/ProductItemContext';
import { ProductAttributes } from '../ProductAttributes';
import { ProductMatrixSectionGeneral } from '../ProductMatrix/ProductMatrixSectionGeneral';
import { ProductPrice } from '../ProductPrice';
import { UnitSwitcher } from '../UnitSwitcher';

import { ProductLineButtons } from './ProductLineButtons';

const ProductLineComponent = () => {
  const ref = React.useRef<HTMLDivElement>(null);
  const { productInfo } = useProductItemContext();
  const defaultImageSize = useSelector(getDefaultImageSize);
  const aspectRatio = useSelector(getDefaultImageAspectRatio);
  const filteredDelwinIds = useSelector(getFilteredDelwinIds);
  const productImage = React.useMemo(
    () => getProductImageOrPlaceholderBySize(productInfo, defaultImageSize),
    [productInfo, defaultImageSize],
  );
  const nameRef = React.useRef<HTMLHeadingElement>(null);
  const isNameOverflowing = useIsOverflowing(nameRef, { scrollHeightAdjustor: 3 });
  const filteredDelwinIdsWithCurrentStockTypes = useDelwinIdsWithCurrentStockType(filteredDelwinIds);
  const allDelwinIdsWithCurrentStockType = useDelwinIdsWithCurrentStockType();
  const variants = React.useMemo(() => (isEmpty(productInfo) ? [] : ProductsLogic.getAllOrderedVariants(productInfo)), [productInfo]);
  const width = useOffsetWidth(ref);

  const delwinToShowIds =
    isEmpty(filteredDelwinIdsWithCurrentStockTypes) ? allDelwinIdsWithCurrentStockType : filteredDelwinIdsWithCurrentStockTypes;

  const productVariants = React.useMemo(() => getVariantsForMatrix(variants, productInfo.product), [productInfo.product, variants]);
  const link = getUrlForProductAndVariant({ productId: productInfo.product, variantId: productInfo.variant });

  return (
    <div className="mx-auto rounded-regular bg-white p-4 lg:px-8 lg:py-5">
      <div className="-m-1 flex flex-col gap-3 gap-x-8 overflow-hidden p-1 sm:grid sm:grid-cols-[auto,1fr]">
        <div className="w-48 shrink-0">
          <Link to={link} className="block rounded-regular focus-visible:focus-outline">
            <CustomImage aspectRatio={aspectRatio} src={productImage.src} placeholderClassName="rounded-regular overflow-hidden" />
          </Link>
        </div>
        <div className="-m-1 flex flex-grow flex-col overflow-hidden p-1">
          <div ref={ref} className="mb-3">
            <Link to={link} className="block cursor-pointer rounded-small focus-visible:focus-outline">
              <Tooltip overlayClassName="max-w-52" content={isNameOverflowing && productInfo.name} disableOnTouchScreen>
                <div>
                  <span ref={nameRef} className="mb-1 line-clamp-2 break-words heading-2">
                    {productInfo.name}
                  </span>
                </div>
              </Tooltip>
              <ProductPrice productInfo={productInfo} parentWidth={width} />
            </Link>
          </div>
          <ProductAttributes product={productInfo} />
          <div className="mt-4">
            <ProductLineButtons product={productInfo} />
          </div>
          {isSwitchAvailable(productInfo.tableMappings) && (
            <div className="mt-auto flex justify-end pt-4">
              <UnitSwitcher tableMappings={productInfo.tableMappings} defaultUnit={productInfo.defaultLocalizedChart} />
            </div>
          )}
        </div>
      </div>
      <div className="mt-4 overflow-hidden">
        <ProductMatrixSectionGeneral variants={productVariants} attributesSource="listing" showOnlyDeliveryWindows={delwinToShowIds} />
      </div>
    </div>
  );
};

interface ProductLineProps {
  productInfo: Product.Full;
}

export const ProductLine = React.memo(({ productInfo }: ProductLineProps) => {
  return (
    <ProductItemContextProvider productInfo={productInfo} getProductUrl={getUrlForProductAndVariant}>
      <ProductLineComponent />
    </ProductItemContextProvider>
  );
});
